import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { appMonthNameYear } from '@/utils/appDateFormat';
import {
  WpsConfirmWarning,
  WpsDueMonthView,
  WpsHoldReason,
  WpsLineReleaseReason,
  WpsMonthStatus,
  WpsVoidReason,
  WpsZone,
} from '../../../../models/payroll';

// Every sentence on the UAE WPS screens is written out as one key. The server sends codes, names and counts, and these
// functions turn them into text, so nothing is composed from fragments and the i18n audit can see each key.

export type WpsTone = 'ok' | 'warn' | 'error' | 'neutral';

const NAMES_IN_A_SENTENCE = 20;

/** Every zone value has a tone. Green is styled as fine only when the month really reads marked as submitted. */
export const wpsZoneTone = (zone: WpsZone | undefined, monthStatus?: WpsMonthStatus | null): WpsTone => {
  switch (zone) {
    case 'Green':
      return monthStatus === 'Submitted' ? 'ok' : 'neutral';
    case 'Amber':
    case 'Attention':
      return 'warn';
    case 'Red':
    case 'Overdue':
      return 'error';
    default:
      return 'neutral';
  }
};

/** The zones where the month's clock is really running, so the legal timeline may mark a position. */
export const wpsClockRunning = (zone: WpsZone | undefined): boolean =>
  zone === 'Amber' || zone === 'Red' || zone === 'Overdue';

export const WPS_TONE_BADGE: Record<WpsTone, 'success' | 'warning' | 'error' | 'neutral'> = {
  ok: 'success',
  warn: 'warning',
  error: 'error',
  neutral: 'neutral',
};

/** Mirrors WpsHoldReasonRules.ClosesMonth on the server: these answer the month, the others keep it open. */
export const wpsHoldClosesMonth = (reason: WpsHoldReason): boolean =>
  reason === 'PaidAnotherWay' ||
  reason === 'DocumentedUnpaidLeave' ||
  reason === 'WorkAbandonmentReported' ||
  reason === 'CourtWageDispute';

/** The month a UAE screen opens on: the due month while open, else the oldest open month, else the current month. */
export const defaultWpsMonth = (due: WpsDueMonthView | null | undefined, today: Date): { year: number; month: number } => {
  if (due?.open) return { year: due.year, month: due.month };
  const oldest = due?.olderOpenMonths?.[0];
  if (oldest) return { year: oldest.year, month: oldest.month };
  return { year: today.getFullYear(), month: today.getMonth() + 1 };
};

/** The citation is the same on the WPS Center and the Compliance Center overview. */
export const useMohreCitation = (): { code: string; description: string } => {
  const { t } = useTranslation();
  return {
    code: t('payroll.wps.uae.law.citationCode'),
    description: t('payroll.complianceCenter.lawRef.wps'),
  };
};

export const useWpsLabels = () => {
  const { t, i18n } = useTranslation();

  return useMemo(() => {
    const names = (list: string[], more = 0): string => {
      const joined = new Intl.ListFormat(i18n.language, { style: 'long', type: 'conjunction' }).format(list);
      return more > 0 ? t('payroll.wps.uae.names.withMore', { names: joined, more }) : joined;
    };

    /** For a sentence the client builds from a long list, so one warning cannot run to fifty names. */
    const cappedNames = (list: string[]): string => names(list.slice(0, NAMES_IN_A_SENTENCE), Math.max(0, list.length - NAMES_IN_A_SENTENCE));

    const zone = (value: WpsZone | undefined): string => {
      switch (value) {
        case 'Green': return t('payroll.wps.uae.zone.green');
        case 'Amber': return t('payroll.wps.uae.zone.amber');
        case 'Red': return t('payroll.wps.uae.zone.red');
        case 'Overdue': return t('payroll.wps.uae.zone.overdue');
        case 'Attention': return t('payroll.wps.uae.zone.attention');
        case 'Off': return t('payroll.wps.uae.zone.off');
        case 'Neutral': return t('payroll.wps.uae.zone.neutral');
        default: return '';
      }
    };

    /** The short wording, for a card or a badge. The status line in the WPS Center carries the numbers. */
    const monthStatusShort = (status: WpsMonthStatus | null | undefined): string => {
      switch (status) {
        case 'Submitted': return t('payroll.wps.uae.status.submitted');
        case 'SubmittedBelowShare': return t('payroll.wps.uae.statusShort.belowShare');
        case 'PartlySubmitted': return t('payroll.wps.uae.statusShort.partly');
        case 'NotSubmitted': return t('payroll.wps.uae.status.notSubmitted');
        case 'NothingFiled': return t('payroll.wps.uae.statusShort.nothingFiled');
        case 'NoEmployees': return t('payroll.wps.uae.status.noEmployees');
        default: return '';
      }
    };

    const issue = (code: string): string => {
      switch (code) {
        case 'labourCardMissing': return t('payroll.wps.uae.issue.labourCardMissing');
        case 'labourCardFormatInvalid': return t('payroll.wps.uae.issue.labourCardFormatInvalid');
        case 'ibanMissing': return t('payroll.wps.uae.issue.ibanMissing');
        case 'ibanFormatInvalid': return t('payroll.wps.uae.issue.ibanFormatInvalid');
        case 'ibanChecksumInvalid': return t('payroll.wps.uae.issue.ibanChecksumInvalid');
        case 'netPayNotPositive': return t('payroll.wps.uae.issue.netPayNotPositive');
        case 'amountUnreadable': return t('payroll.wps.uae.issue.amountUnreadable');
        case 'fxRateMissing': return t('payroll.wps.uae.issue.fxRateMissing');
        case 'payComponentNegative': return t('payroll.wps.uae.issue.payComponentNegative');
        case 'employeeBankRoutingMissing': return t('payroll.wps.uae.issue.employeeBankRoutingMissing');
        case 'payslipAfterExit': return t('payroll.wps.uae.issue.payslipAfterExit');
        default: return t('payroll.wps.uae.issue.other');
      }
    };

    const holdReason = (reason: WpsHoldReason | string | null | undefined): string => {
      switch (reason) {
        case 'PaidAnotherWay': return t('payroll.wps.uae.holdReason.paidAnotherWay');
        case 'DocumentedUnpaidLeave': return t('payroll.wps.uae.holdReason.documentedUnpaidLeave');
        case 'WorkAbandonmentReported': return t('payroll.wps.uae.holdReason.workAbandonmentReported');
        case 'CourtWageDispute': return t('payroll.wps.uae.holdReason.courtWageDispute');
        case 'BankDetailsPending': return t('payroll.wps.uae.holdReason.bankDetailsPending');
        default: return t('payroll.wps.uae.reason.other');
      }
    };

    const outsideReason = (reason: string | null | undefined): string => {
      switch (reason) {
        case 'Cash': return t('payroll.wps.uae.outsideReason.cash');
        case 'Cheque': return t('payroll.wps.uae.outsideReason.cheque');
        case 'ForeignAccount': return t('payroll.wps.uae.outsideReason.foreignAccount');
        case 'ForeignStaffPaidAbroad': return t('payroll.wps.uae.outsideReason.foreignStaffPaidAbroad');
        default: return t('payroll.wps.uae.reason.other');
      }
    };

    const voidReason = (reason: WpsVoidReason | string | null | undefined): string => {
      switch (reason) {
        case 'NeverSentToBank': return t('payroll.wps.uae.voidReason.neverSentToBank');
        case 'BankRejectedFile': return t('payroll.wps.uae.voidReason.bankRejectedFile');
        case 'BankReturnedMoney': return t('payroll.wps.uae.voidReason.bankReturnedMoney');
        default: return t('payroll.wps.uae.reason.other');
      }
    };

    const lineReason = (reason: WpsLineReleaseReason | string | null | undefined): string => {
      switch (reason) {
        case 'BankRejectedLine': return t('payroll.wps.uae.lineReason.bankRejectedLine');
        case 'BankReturnedMoney': return t('payroll.wps.uae.lineReason.bankReturnedMoney');
        case 'FileNotSent': return t('payroll.wps.uae.lineReason.fileNotSent');
        default: return t('payroll.wps.uae.reason.other');
      }
    };

    /** The server sends the months of a warning as yyyy-MM, invariant, so the words stay on this side. */
    const months = (w: WpsConfirmWarning): string =>
      names(w.names.map(value => {
        const [year, month] = value.split('-').map(Number);
        return year && month ? appMonthNameYear(new Date(year, month - 1, 1)) : value;
      }), w.moreCount);

    /** A confirm warning as one sentence that names the people it is about. */
    const warning = (w: WpsConfirmWarning): string => {
      const who = names(w.names, w.moreCount);
      switch (w.code) {
        case 'paidOutsideNotExemption': return t('payroll.wps.uae.paidOutside.notExemption');
        case 'paidOutsideForeignStaffLimits': return t('payroll.wps.uae.paidOutside.foreignStaffLimits');
        case 'paidOutsideMonthsToOutside': return t('payroll.wps.uae.warn.paidOutsideMonthsToOutside', { months: months(w) });
        case 'paidOutsideMonthsToFile': return t('payroll.wps.uae.warn.paidOutsideMonthsToFile', { months: months(w) });
        case 'settingsClockOff': return t('payroll.wps.uae.warn.settingsClockOff');
        case 'settingsHoldBackOffActiveHolds': return t('payroll.wps.uae.warn.settingsHoldBackOffActiveHolds', { names: who });
        case 'settingsPaidOutsideOffRunning': return t('payroll.wps.uae.warn.settingsPaidOutsideOffRunning', { names: who });
        case 'settingsThresholdBelowLegal': return t('payroll.wps.uae.warn.settingsThresholdBelowLegal');
        case 'markPaidHeld': return t('payroll.wps.uae.warn.markPaidHeld', { names: who });
        case 'markPaidHeldRefused': return t('payroll.wps.uae.warn.markPaidHeldRefused', { names: who });
        case 'markPaidToFile': return t('payroll.wps.uae.warn.markPaidToFile', { names: who });
        case 'markSubmittedHasReleased': return t('payroll.wps.uae.warn.markSubmittedHasReleased', { names: who });
        case 'downloadVoided':
          return w.names.length > 0
            ? t('payroll.wps.uae.warn.downloadVoided', { names: who })
            : t('payroll.wps.uae.warn.downloadVoidedLegacy', { count: w.count });
        case 'downloadHasReleased': return t('payroll.wps.uae.warn.downloadHasReleased', { names: who });
        case 'voidReturnsToFile':
          return w.names.length > 0
            ? t('payroll.wps.uae.warn.voidReturnsToFile', { count: w.count, names: who })
            : t('payroll.wps.uae.warn.voidReturnsToFileLegacy', { count: w.count });
        case 'voidMarkedPaid': return t('payroll.wps.uae.warn.voidMarkedPaid', { names: who });
        case 'voidFinalSettlement':
        case 'releaseFinalSettlement':
          return t('payroll.wps.uae.warn.finalSettlement', { names: who });
        case 'releaseMayPayTwice': return t('payroll.wps.uae.warn.releaseMayPayTwice', { names: who });
        case 'holdMarkedPaid': return t('payroll.wps.uae.warn.holdMarkedPaid', { names: who });
        case 'holdEveryoneCovers': return t('payroll.wps.uae.warn.holdEveryoneCovers', { names: who });
        case 'holdEveryoneOpenHoldsStay': return t('payroll.wps.uae.warn.holdEveryoneOpenHoldsStay', { names: who });
        case 'holdEveryoneNobody': return t('payroll.wps.uae.warn.holdEveryoneNobody');
        case 'releaseEveryoneReturns': return t('payroll.wps.uae.warn.releaseEveryoneReturns', { names: who });
        default: return t('payroll.wps.uae.warn.generic', { names: who });
      }
    };

    const monthOf = (period: string): string => {
      const [y, m] = period.split('-').map(Number);
      return appMonthNameYear(new Date(y, m - 1));
    };

    // Compliance events are stored as English text with no code beside them, so the two WPS event types are read back
    // here. The text is fixed in PayrollComplianceService; anything that does not match shows as stored.
    const eventTitle = (eventType: string, title: string): string => {
      if (eventType === 'WpsDeadline') {
        if (title === 'WPS deadline: earlier month still open') return t('payroll.wps.uae.event.earlierOpen');
        const zoneMatch = /^WPS deadline: (\w+) zone$/.exec(title);
        if (zoneMatch) return t('payroll.wps.uae.event.zone', { zone: zone(zoneMatch[1] as WpsZone) });
      }
      if (eventType === 'SifIssue') {
        const sif = /^SIF pre-flight: (\d+) blocking issue\(s\)$/.exec(title);
        if (sif) return t('payroll.wps.uae.event.sifTitle', { n: Number(sif[1]) });
      }
      return title;
    };

    const eventDetail = (eventType: string, detail: string): string => {
      if (eventType === 'SifIssue') {
        const parts = detail.split('; ').map(part => /^(\w+): (\d+) employee\(s\)$/.exec(part));
        if (parts.length === 0 || parts.some(p => !p)) return detail;
        return parts.map(p => t('payroll.wps.uae.event.sifPart', { issue: issue(p![1]), n: Number(p![2]) })).join('; ');
      }
      if (eventType !== 'WpsDeadline') return detail;
      const sentences: string[] = [];
      let rest = detail;
      const take = (pattern: RegExp): RegExpExecArray | null => {
        const match = pattern.exec(rest);
        if (match) rest = rest.slice(match[0].length).trimStart();
        return match;
      };
      const nothing = take(/^(\d{4}-\d{2}): nothing filed through WPS\./);
      if (nothing) sentences.push(t('payroll.wps.uae.event.nothingFiled', { month: monthOf(nothing[1]) }));
      const below = take(/^(\d{4}-\d{2}): marked as submitted, below the (\d+)% share\./);
      if (below) sentences.push(t('payroll.wps.uae.event.belowShare', { month: monthOf(below[1]), threshold: below[2] }));
      const late = take(/^(\d{4}-\d{2}): (\d+) employee\(s\) still to file\. (-?\d+) day\(s\) after the due date\./);
      if (late) sentences.push(t('payroll.wps.uae.event.outstanding', { month: monthOf(late[1]), n: Number(late[2]), days: Number(late[3]) }));
      const earlier = take(/^Earlier months with employee\(s\) still to file: (.+)\.$/);
      if (earlier) {
        const months = earlier[1].split(', ').map(item => {
          const parts = /^(\d{4}-\d{2}) \((\d+)\)$/.exec(item);
          return parts ? t('payroll.wps.uae.event.monthCount', { month: monthOf(parts[1]), n: Number(parts[2]) }) : item;
        });
        sentences.push(t('payroll.wps.uae.event.earlierMonths', { months: months.join(', ') }));
      }
      return rest.length > 0 || sentences.length === 0 ? detail : sentences.join(' ');
    };

    return { names, cappedNames, zone, monthStatusShort, issue, holdReason, outsideReason, voidReason, lineReason, warning, eventTitle, eventDetail };
  }, [t, i18n.language]);
};
